import Image from 'next/image';
import { useRouter } from 'next/router';
import cn from 'classnames';
import React, { MouseEventHandler, useMemo, useRef } from 'react';
import { Language, Post, PostParentKey } from 'common/interfaces/api';
import { MediaType } from 'common/interfaces/api';
import Icon from 'components/common/Icon';
import RichContent from 'components/common/RichContent';
import styles from './SmallPost.module.scss';
import { textWithoutEnter, timeSince, useMediaQuery } from 'common/utils';
import ViewedContent from '../ViewedContent';
import { adsTrackingCall } from 'service/ads_tracking';
import classNames from 'classnames';

export type Props = {
  post: Post;
  parent?: PostParentKey | string;
  className?: string;
};

function UnmemoizedSmallPostBigThumbItem({ post, parent, className }: Props) {
  // Local post state so that edited content is shown immediately
  const router = useRouter();
  const isSmallScreen = useMediaQuery('(max-width: 1100px)');
  const postDetailsUrl = `/${post.user_info.username}/posts/${post.id}`;
  const mediaContent = useMemo(() => {
    let media: JSX.Element;
    if (post?.streaming && post?.streaming?.thumbnail) {
      media = (
        <div className={styles.media} onContextMenu={(e) => e.preventDefault()}>
          <Image
            src={post?.streaming?.thumbnail}
            layout="fill"
            objectFit="cover"
            alt="thumbnail"
          />
          <div className={styles.playBtn}>
            <Icon name="play" width={0.45 * 80} height={0.45 * 80} />
          </div>
        </div>
      );
    } else if (post?.media && post.media[0]) {
      if (post.media[0].type === MediaType.Image) {
        const isBlur =
          !post.content_access?.is_authorized && !!post.media[0].data.blur;
        media = (
          <div
            className={cn(styles.media, {
              [styles.blurred]: isBlur,
            })}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className={styles.imageWrapper}>
              <Image
                src={post.media[0].data.url}
                layout="fill"
                objectFit="cover"
                alt="thumbnail"
              />
            </div>
          </div>
        );
      } else if (post.media[0].type === MediaType.Video) {
        media = (
          <div
            className={styles.media}
            onContextMenu={(e) => e.preventDefault()}
          >
            <Image
              src={post.media[0].data.thumbnail_url}
              layout="fill"
              objectFit="cover"
              alt="thumbnail"
            />
            <div className={styles.playBtn}>
              <Icon name="play" width={0.45 * 80} height={0.45 * 80} />
            </div>
          </div>
        );
      } else if (post.media[0].type === MediaType.Link) {
        if (post.is_affiliate) media = <MediaAds post={post} parent={parent} />;
        else
          media = (
            <div className={styles.media}>
              <Image
                src={post.media[0].data.thumbnail_url}
                layout="fill"
                objectFit="cover"
                alt="thumbnail"
              />
            </div>
          );
      } else if (post.media[0].type === MediaType.Audio) {
        media = (
          <div
            className={styles.media}
            style={{
              borderRadius: '6px',
              background: '#0abc71',
            }}
          >
            {post.media[0].data.thumbnail_url && (
              <Image
                src={post.media[0].data.thumbnail_url}
                layout="fill"
                objectFit="cover"
              />
            )}
            <div className={styles.playBtn}>
              <Icon name="audio-icon" width={30} height={33} />
            </div>
          </div>
        );
      }
    } else {
      media = (
        <div className={styles.media}>
          <img
            src={post.user_info?.avatar_url}
            alt={post.user_info?.name}
            className={styles.avatar}
          />
        </div>
      );
    }
    return media;
  }, [parent, post]);

  // Right side components
  const timeSinceText = timeSince(new Date(post.created_at), Language.Ja);
  // Handle clicking on post
  const postRef = useRef<HTMLDivElement>();
  const handlePostClick: MouseEventHandler = (e) => {
    const selection = window.getSelection();
    if (
      selection.type != 'Range' &&
      // prevent portal from handling click event
      postRef.current.contains(e.target as HTMLElement)
    ) {
      void router.push(postDetailsUrl);
    }
  };

  const textContentWithoutEnter = useMemo(() => {
    return textWithoutEnter(post.text)?.slice(0, isSmallScreen ? 100 : 40);
  }, [post.text, isSmallScreen]);
  return (
    <div
      className={classNames(styles.smallPostWrapper, className || '')}
      onClick={handlePostClick}
      ref={postRef}
    >
      <div className={styles.thumbnail}>{mediaContent}</div>
      <div className={styles.contentWrapper}>
        <div className={styles.postTextWrapper}>
          <div className={styles.postText}>
            <RichContent text={textContentWithoutEnter || '無題'} />
          </div>
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userNameText} title={post.user_info?.name}>
            {post.user_info?.name}
          </div>
          <div className={styles.userJobText} title={post.user_info?.job}>
            {post.is_affiliate ? 'Sponsored' : timeSinceText}
          </div>
        </div>
      </div>
    </div>
  );
}

const MediaAds = ({
  post,
  parent,
}: {
  post: Post;
  parent: PostParentKey | string;
}) => {
  const handleFirstImpression = () => {
    adsTrackingCall({
      type: 'ads_impression',
      parentType: parent,
      postId: post.id,
    });
  };

  const handleClickLinkAds = () => {
    adsTrackingCall({
      type: 'ads_click',
      parentType: parent,
      postId: post.id,
    });
  };

  return (
    <ViewedContent firstViewCallback={handleFirstImpression}>
      <a onClick={() => handleClickLinkAds()}>
        <div className={styles.media}>
          <Image
            src={post.media[0].data.thumbnail_url}
            layout="fill"
            objectFit="cover"
            alt="thumbnail"
          />
        </div>
      </a>
    </ViewedContent>
  );
};
export default React.memo(UnmemoizedSmallPostBigThumbItem);
